import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { environment } from '../../../environments/environment';
import { User } from '../models/user';
import {
  IAM_PROFILE_KEY,
  StorageService,
  USER_KEY
} from '../service/storage.service';
import { IamHeaderService } from '../service/iam-header.service';
import { AuthService } from '../service/auth.service';
 
export const headerImplInterceptor: HttpInterceptorFn = (req, next) => {
  const storageService = inject(StorageService);
  const iamHeaderService = inject(IamHeaderService);
  const authService = inject(AuthService);
 
  const token = authService.getAccessToken() ?? '';
  const user = storageService.getObject(USER_KEY) as User;
 
  if (isIamAuthUrl(req.url)) {
    return next(
      req.clone({
        withCredentials: true,
        setHeaders: buildIamHeaders(token, iamHeaderService),
      })
    );
  }
 
  if (openUrl(req.url) || !includedUrl(req.url)) {
    return next(req);
  }
 
  if (!user || !token) {
    return next(req);
  }
 
  return next(
    req.clone({
      setHeaders: buildIncludedHeaders(
        req.url,
        user,
        token,
        storageService
      ),
    })
  );
};
 
function buildIamHeaders(
  token: string | null,
  iamHeaderService: IamHeaderService
): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-Device-Context': iamHeaderService.getDeviceContext(),
    'X-Req-Nonce': iamHeaderService.generateNonce(),
    'X-Tenant-Id': environment.iam.tenantId,
    'Idempotency-Key': iamHeaderService.generateIdempotencyKey(),
  };
 
  if (token) {
    headers['Authorization'] = token.startsWith('Bearer ')
      ? token
      : `Bearer ${token}`;
  }
 
  return headers;
}
 
function getInstallerId(
  user: User,
  storageService: StorageService
): string {
  if (user.installer_id) {
    return user.installer_id;
  }
 
  const cachedProfile = storageService.getObject(IAM_PROFILE_KEY) as {
    identity?: {
      installerId?: string;
      installer_id?: string;
    };
  } | null;
 
  return (
    cachedProfile?.identity?.installerId ||
    cachedProfile?.identity?.installer_id ||
    ''
  );
}
 
function buildIncludedHeaders(
  url: string,
  user: User,
  token: string,
  storageService: StorageService
): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    accept: 'application/json',
    authorization: token.startsWith('Bearer ')
      ? token
      : `Bearer ${token}`,
    'X-UserID': user.id,
  };
 
  if (
    url.includes(
      environment.externalUrls.system_integration.baseUrl
    )
  ) {
    const installerId = getInstallerId(user, storageService);
 
    if (installerId) {
      headers['installerId'] = installerId;
    }
  }
 
  return headers;
}
 
function includedUrl(url: string): boolean {
  return (
    url.includes(environment.externalUrls.user.baseUrl) ||
    url.includes(environment.externalUrls.product.baseUrl) ||
    url.includes(
      environment.externalUrls.system_integration.baseUrl
    )
  );
}
 
function isIamAuthUrl(url: string): boolean {
  const isIamUrl = url.startsWith(environment.iam.baseUrl);
  const isUsersUrl = url.startsWith(environment.users.baseUrl);
 
  if (!isIamUrl && !isUsersUrl) {
    return false;
  }
 
  const iamPaths = [
    '/send',
    '/verify',
    '/resend',
    '/login',
    '/refresh',
    '/logout',
    '/users/profile',
    '/otp/send',
    '/api/v1/users/onboarding',
  ];
 
  return iamPaths.some(path => url.includes(path));
}
 
 
 
function openUrl(url: string): boolean {
  const paths = [
    'otp/validateNew',
  ];
 
  return paths.some(path => url.endsWith(path));
}
 